import { AsyncSelect } from 'chakra-react-select'
import Cookies from 'js-cookie'
import groupBy from 'lodash/groupBy'
import sortBy from 'lodash/sortBy'
import type { ChangeEvent, FC, FocusEvent } from 'react'
import { useRef } from 'react'

import useGetObject from '@app/hooks/useGetObject'
import { SELECTED_SLACK_CHANNEL_COOKIE } from '@app/lib/globals'
import { useStore } from '@app/store'
import client from '@graphql/client'
import { SlackChannelsAutocomplete } from '@graphql/documents/slack_channel.graphql'

const slackChannelToPickerItem = (slackChannel) => ({
  value: slackChannel.id,
  label: slackChannel.name,
  channelId: slackChannel.channelId,
  type: slackChannel.isIm ? 'Direct Message' : 'Channel'
})

interface Props {
  placeholder?: string
  defaultValue?: string
  prepopulateFromCookie?: boolean
  clearable?: boolean
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void
  name: string
}

const SlackChannelAutocomplete: FC<Props> = ({
  defaultValue,
  placeholder = 'Select a slack channel',
  prepopulateFromCookie = false,
  clearable = false,
  onBlur = () => {},
  onChange = () => {},
  ...rest
}) => {
  const slackChannel = useGetObject(defaultValue, 'slackChannel')
  let autocompleteDefaultValue = slackChannel ? slackChannelToPickerItem(slackChannel) : null
  const cookiesApi = Cookies.withAttributes({ sameSite: 'lax' })
  const bulkAdd = useStore.use.bulkAdd()
  const ref = useRef(undefined)

  const loadOptions = async (inputValue) => {
    const { data } = await client.query(SlackChannelsAutocomplete, { query: inputValue }).toPromise()
    const channelsWithoutArchived = data?.slackChannelsAutocomplete.filter((channel) => !channel.isArchived)

    if (data?.slackChannelsAutocomplete) {
      bulkAdd(data.slackChannelsAutocomplete)
    }

    const options = channelsWithoutArchived?.map(slackChannelToPickerItem)
    const groups = groupBy(options, 'type')

    const groupedOptions = []
    Object.keys(groups).forEach((group) => {
      groupedOptions.push({ label: group, options: sortBy(groups[group], ['label', 'toLowerCase']) })
    })

    return groupedOptions
  }

  if (prepopulateFromCookie) {
    const cookie = cookiesApi.get(SELECTED_SLACK_CHANNEL_COOKIE)

    try {
      const parsedCookie = cookie ? JSON.parse(cookie) : null

      if (parsedCookie && parsedCookie?.label) {
        autocompleteDefaultValue = parsedCookie
      }
    } catch (e) {
      console.error('Error parsing cookie:', e) /* eslint-disable-line no-console */
    }
  }

  const handleChange = (e) => {
    if (e) {
      const cookie = JSON.stringify(e)
      cookiesApi.set(SELECTED_SLACK_CHANNEL_COOKIE, cookie)
    }

    /* @ts-expect-error These are fake types so we can use the inline-updater */
    onChange?.({ target: { name: rest?.name, value: e?.value || '' } })
  }

  const handleBlur = (_e) => {
    const value = ref?.current?.getValue()[0]?.value

    /* @ts-expect-error These are fake types so we can use the inline-updater */
    onBlur?.({ target: { name: rest?.name, value: value || '' } })
  }

  return (
    <AsyncSelect
      ref={ref}
      placeholder={placeholder}
      defaultValue={autocompleteDefaultValue}
      defaultOptions
      cacheOptions
      loadOptions={loadOptions}
      isClearable={clearable}
      onChange={handleChange}
      onBlur={handleBlur}
      {...rest}
    />
  )
}

export default SlackChannelAutocomplete
